import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ShieldCheck, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const VERTICAL_PRESETS = {
  "law-enforcement": "law_enforcement",
  "fire-ems": "fire_service",
  "corrections": "corrections",
};

const VERTICALS = [
  ["law_enforcement", "Law Enforcement"], ["fire_service", "Fire Service"],
  ["ems", "EMS"], ["dispatch_psap", "Dispatch / PSAP"], ["corrections", "Corrections"],
  ["school_safety", "School Safety"], ["military", "Military"], ["emergency_medicine", "Emergency Medicine"],
];

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

// Phase 1 launch: magic-link is the sole authentication method.
// Google OAuth has been intentionally removed from the primary user journey;
// the /auth/google-url endpoint + /auth/callback route remain dormant for a
// possible future re-enable, but are NOT wired to any UI here.
const requestMagicLink = async (email, intakeId = null) => {
  const redirectUri = window.location.origin + "/auth/verify";
  const payload = { email, redirect_uri: redirectUri };
  if (intakeId) payload.intake_id = intakeId;
  await api.post("/auth/magic-link/request", payload);
};

export default function TrialSignIn({ mode = "trial" }) {
  const { vertical: urlVertical } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const tab = mode; // route-driven: /start-trial (+/trial aliases) vs /signin
  const presetVertical = urlVertical ? VERTICAL_PRESETS[urlVertical] : null;

  const [form, setForm] = useState({
    agency_name: "", email: "", city: "", state: "",
    first_name: "", last_name: "",
    vertical: presetVertical || "law_enforcement",
  });
  const [submitting, setSubmitting] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [usePassword, setUsePassword] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agency_name.trim() || !form.email.trim() || !form.city.trim() || !form.state.trim()) {
      toast.error("Agency name, email, city, and state are required.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/trial/intake", {
        ...form,
        utm_source: params.get("utm_source") || null,
        utm_campaign: params.get("utm_campaign") || null,
        utm_medium: params.get("utm_medium") || null,
      });
      // Carry intake_id through the magic-link round-trip.
      // Primary path: bind it to the token server-side (works cross-device / after
      // sessionStorage loss). Fallback path via sessionStorage stays as a safety net
      // for anyone still on an old client version — the linking helper is idempotent.
      sessionStorage.setItem("haruki_intake_id", r.data.intake_id);
      await requestMagicLink(form.email.trim().toLowerCase(), r.data.intake_id);
      setLinkSent(true);
      setSubmitting(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start trial.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border bg-background/90 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="rounded-md p-1.5" style={{ background: "hsl(var(--haruki-leaf))" }}>
              <ShieldCheck className="h-4 w-4 text-white" />
            </div>
            <span className="font-heading text-base font-bold tracking-tight">Haruki Health</span>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← Back to site</Link>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-12 lg:py-16">
        <div className="text-center">
          <div className="mb-3 text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "hsl(var(--haruki-leaf-deep))" }}>
            Tactical Resilience Training Platform
          </div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">
            {tab === "trial"
              ? <>Start your <span style={{ color: "hsl(var(--haruki-leaf))" }}>free trial</span>.</>
              : <>Welcome back to <span style={{ color: "hsl(var(--haruki-leaf))" }}>Haruki</span>.</>}
          </h1>
          <p className="mt-4 text-base text-muted-foreground">
            {tab === "trial"
              ? "21 days of scenario-based resilience training. No password, no credit card — just your email."
              : "Sign in to continue your training, view your history, or upgrade your plan."}
          </p>
        </div>

        {/* Cross-link to the other flow */}
        <div className="mt-6 text-center text-sm text-muted-foreground" data-testid="auth-cross-link">
          {tab === "trial" ? (
            <>Already have an account?{" "}
              <Link to="/signin" data-testid="trial-to-signin-link"
                    className="font-semibold underline-offset-2 hover:underline"
                    style={{ color: "hsl(var(--haruki-leaf-deep))" }}>
                Sign in
              </Link>
            </>
          ) : (
            <>New to Haruki?{" "}
              <Link to="/start-trial" data-testid="signin-to-trial-link"
                    className="font-semibold underline-offset-2 hover:underline"
                    style={{ color: "hsl(var(--haruki-leaf-deep))" }}>
                Start your free trial
              </Link>
            </>
          )}
        </div>

        {/* TRIAL FORM */}
        {tab === "trial" && !linkSent && (
          <Card className="mt-8 haruki-fade-in">
            <CardContent className="p-6 lg:p-8">
              <form onSubmit={submit} className="space-y-5" data-testid="trial-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Agency name *">
                    <Input data-testid="trial-agency-name" value={form.agency_name}
                           onChange={upd("agency_name")} placeholder="e.g. Portland Police Bureau" />
                  </Field>
                  <Field label="Email address *">
                    <Input data-testid="trial-email" type="email" value={form.email}
                           onChange={upd("email")} placeholder="you@agency.gov" />
                  </Field>
                  <Field label="City *">
                    <Input data-testid="trial-city" value={form.city} onChange={upd("city")} />
                  </Field>
                  <Field label="State *">
                    <Select value={form.state} onValueChange={upd("state")}>
                      <SelectTrigger data-testid="trial-state"><SelectValue placeholder="Choose state" /></SelectTrigger>
                      <SelectContent>{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="First name (optional)">
                    <Input data-testid="trial-first-name" value={form.first_name} onChange={upd("first_name")} />
                  </Field>
                  <Field label="Last name (optional)">
                    <Input data-testid="trial-last-name" value={form.last_name} onChange={upd("last_name")} />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Discipline">
                      <Select value={form.vertical} onValueChange={upd("vertical")}>
                        <SelectTrigger data-testid="trial-vertical"><SelectValue /></SelectTrigger>
                        <SelectContent>{VERTICALS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                  </div>
                </div>

                <Button
                  data-testid="trial-submit-button"
                  type="submit" disabled={submitting} size="lg"
                  className="w-full text-base font-bold shadow-lg"
                  style={{ background: "hsl(var(--haruki-leaf))", color: "white",
                           boxShadow: "0 6px 24px -6px hsl(var(--haruki-leaf) / 0.5)" }}
                >
                  {submitting
                    ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending link…</>
                    : <>Start My 21-Day Trial <ArrowRight className="ml-2 h-4 w-4" /></>}
                </Button>

                <p className="text-center text-xs text-muted-foreground">
                  We&apos;ll email you a secure one-time sign-in link. No password required.
                </p>
              </form>
            </CardContent>
          </Card>
        )}

        {/* SUCCESS — magic link sent */}
        {tab === "trial" && linkSent && (
          <Card className="mt-8 haruki-fade-in" data-testid="magic-link-sent-card">
            <CardContent className="space-y-4 p-8 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full"
                   style={{ background: "hsl(var(--haruki-leaf) / 0.12)" }}>
                <ShieldCheck className="h-6 w-6" style={{ color: "hsl(var(--haruki-leaf))" }} />
              </div>
              <h2 className="font-heading text-xl font-bold">Check your email</h2>
              <p className="text-sm text-muted-foreground">
                We sent a sign-in link to <strong>{form.email}</strong>. Click it within
                15 minutes to finish setting up your Haruki trial.
              </p>
              <p className="text-xs text-muted-foreground">
                Didn&apos;t get it? Check spam, or{" "}
                <button onClick={() => setLinkSent(false)}
                        className="font-semibold underline-offset-2 hover:underline"
                        style={{ color: "hsl(var(--haruki-leaf-deep))" }}
                        data-testid="magic-link-resend-link">
                  try again
                </button>.
              </p>
            </CardContent>
          </Card>
        )}

        {/* SIGN IN */}
        {tab === "signin" && !linkSent && (
          <Card className="mt-8 haruki-fade-in">
            <CardContent className="space-y-6 p-8">
              <div className="text-center">
                <h2 className="font-heading text-xl font-bold">Welcome back</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Enter your work email — we&apos;ll send you a sign-in link.
                </p>
              </div>
              <form
                data-testid="signin-form"
                noValidate
                onSubmit={async (e) => {
                  e.preventDefault();
                  const email = (form.email || "").trim().toLowerCase();
                  if (!email || !email.includes("@")) {
                    toast.error("Enter a valid email address.");
                    return;
                  }
                  setSubmitting(true);
                  try {
                    if (usePassword) {
                      const r = await api.post("/auth/login-password", { email, password: form.password || "" });
                      const u = r.data.user;
                      const dest = !u.role ? "/role"
                        : u.role === "platform_admin" ? "/platform"
                        : u.role === "demo_admin" ? "/admin"
                        : u.role === "agency_admin" ? "/agency"
                        : !u.disclosure_acknowledged_at ? "/disclosure" : "/app";
                      window.location.replace(dest);
                      return;
                    }
                    sessionStorage.setItem("haruki_signin_email_hint", email);
                    await requestMagicLink(email);
                    setLinkSent(true);
                  } catch (err) {
                    const d = err?.response?.data?.detail;
                    toast.error(typeof d === "string" ? d : (usePassword ? "Could not sign in." : "Could not send sign-in link."));
                  } finally {
                    setSubmitting(false);
                  }
                }}
                className="space-y-5"
              >
                <Field label="Email address">
                  <Input
                    data-testid="signin-email"
                    type="email"
                    value={form.email}
                    onChange={upd("email")}
                    placeholder="you@agency.gov"
                    autoComplete="email"
                  />
                </Field>
                {usePassword && (
                  <Field label="Password">
                    <Input
                      data-testid="signin-password"
                      type="password"
                      value={form.password || ""}
                      onChange={upd("password")}
                      placeholder="Your password"
                      autoComplete="current-password"
                    />
                  </Field>
                )}
                <Button
                  data-testid="signin-magic-button"
                  type="submit" disabled={submitting} size="lg"
                  className="w-full text-base font-semibold"
                  style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
                >
                  {submitting
                    ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {usePassword ? "Signing in…" : "Sending link…"}</>
                    : usePassword
                      ? <>Sign in <ArrowRight className="ml-2 h-4 w-4" /></>
                      : <>Send Login Link <ArrowRight className="ml-2 h-4 w-4" /></>}
                </Button>

                <p className="text-center text-xs text-muted-foreground">
                  {usePassword ? (
                    <button type="button" data-testid="signin-use-magic-toggle"
                            onClick={() => setUsePassword(false)}
                            className="font-semibold underline-offset-2 hover:underline"
                            style={{ color: "hsl(var(--haruki-leaf-deep))" }}>
                      Forgot password? Email me a sign-in link instead
                    </button>
                  ) : (
                    <>No password required. We&apos;ll email you a secure one-time link.{" "}
                      <button type="button" data-testid="signin-use-password-toggle"
                              onClick={() => setUsePassword(true)}
                              className="font-semibold underline-offset-2 hover:underline"
                              style={{ color: "hsl(var(--haruki-leaf-deep))" }}>
                        Use a password instead
                      </button>
                    </>
                  )}
                </p>
              </form>
              <div className="text-center text-xs text-muted-foreground">
                New to Haruki?{" "}
                <Link to="/start-trial" className="font-semibold underline-offset-2 hover:underline"
                      style={{ color: "hsl(var(--haruki-leaf-deep))" }}
                      data-testid="signin-to-trial-link-bottom">
                  Start a 21-day free trial
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* SUCCESS — signin magic link sent */}
        {tab === "signin" && linkSent && (
          <Card className="mt-8 haruki-fade-in" data-testid="signin-magic-link-sent-card">
            <CardContent className="space-y-4 p-8 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full"
                   style={{ background: "hsl(var(--haruki-leaf) / 0.12)" }}>
                <ShieldCheck className="h-6 w-6" style={{ color: "hsl(var(--haruki-leaf))" }} />
              </div>
              <h2 className="font-heading text-xl font-bold">Check your email</h2>
              <p className="text-sm text-muted-foreground">
                If <strong>{form.email}</strong> is associated with a Haruki account, a sign-in link is on its way. The link expires in 15 minutes.
              </p>
              <p className="text-xs text-muted-foreground">
                Wrong email?{" "}
                <button onClick={() => setLinkSent(false)}
                        className="font-semibold underline-offset-2 hover:underline"
                        style={{ color: "hsl(var(--haruki-leaf-deep))" }}
                        data-testid="signin-magic-link-back">
                  Try again
                </button>.
              </p>
            </CardContent>
          </Card>
        )}

        <div className="mt-8 text-center text-xs text-muted-foreground">
          By continuing you agree to Haruki Health&apos;s confidentiality policy. Your agency sees
          completion only — never your responses or scores.
        </div>
      </main>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</Label>
    {children}
  </div>
);
